import { brand } from "../../../../../../config/theme";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { APIProvider, Map, Marker, useMap } from "@vis.gl/react-google-maps";
import type { Geofence } from "../../../../../../store/admin/geofencesStore";
import {
  getTypeText,
  getShapeText,
  typeColors,
  getTypeColor,
} from "../utils/helpers";
import {
  calculateLinearDistance,
  calculateEstimatedTime,
} from "../utils/calculations";
import { buildCorridorRing } from "../../../../../../utils/geofenceCorridor";
import { LINEAR_BUFFER_DEFAULT } from "../types";

interface GeofenceViewerModalProps {
  isOpen: boolean;
  geofences: Geofence[];
  selectedGeofence: Geofence | null;
  isDarkMode: boolean;
  onClose: () => void;
}

// Estilos para scrollbar horizontal
const sliderScrollbarStyles = `
  .slider-scrollbar::-webkit-scrollbar {
    height: 6px;
  }
  .slider-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .slider-scrollbar::-webkit-scrollbar-thumb {
    background: #6b7280;
    border-radius: 3px;
  }
  .slider-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;

// Componente interno para renderizar la forma en el mapa
const GeofenceShape = ({
  geofence,
  onBoundsReady,
}: {
  geofence: Geofence;
  onBoundsReady?: (bounds: google.maps.LatLngBounds) => void;
}) => {
  const map = useMap();
  const circleRef = useRef<google.maps.Circle | null>(null);
  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const axisLineRef = useRef<google.maps.Polyline | null>(null);
  const corridorRef = useRef<google.maps.Polygon | null>(null);

  // Obtener colores basados en el tipo
  const colorSet = useMemo(() => {
    const colors: Record<string, { stroke: string; fill: string }> = {
      warehouse: { stroke: "#2563eb", fill: "#93c5fd" },
      restrictedArea: { stroke: "#ef4444", fill: "#fca5a5" },
      customer: { stroke: "#0891b2", fill: "#a5f3fc" },
      route: { stroke: "#a21caf", fill: "#e9d5ff" },
      maintenance: { stroke: "#f59e42", fill: "#fde68a" },
      authorizedStop: { stroke: brand.primary, fill: brand.primary + "40" },
      default: { stroke: "#6b7280", fill: "#d1d5db" },
    };
    return colors[geofence.type] || colors.default;
  }, [geofence.type]);

  useEffect(() => {
    if (!map) return;

    // Limpiar formas anteriores
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    if (axisLineRef.current) {
      axisLineRef.current.setMap(null);
      axisLineRef.current = null;
    }
    if (corridorRef.current) {
      corridorRef.current.setMap(null);
      corridorRef.current = null;
    }

    const shapeOptions = {
      strokeColor: colorSet.stroke,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: colorSet.fill,
      fillOpacity: 0.35,
      map: map,
    };

    const { shape, coordinates } = geofence;

    if (shape === "circular" && "radius" in coordinates) {
      const center = {
        lat: coordinates.latitude,
        lng: coordinates.longitude,
      };
      const radius = coordinates.radius;

      const circle = new google.maps.Circle({
        ...shapeOptions,
        center,
        radius,
      });
      circleRef.current = circle;

      // Ajustar bounds
      const bounds = circle.getBounds();
      if (bounds) {
        map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
        onBoundsReady?.(bounds);
      }
    } else if (shape === "polygonal" && Array.isArray(coordinates)) {
      const paths = coordinates.map((coord) => ({
        lat: coord.latitude,
        lng: coord.longitude,
      }));

      const polygon = new google.maps.Polygon({
        ...shapeOptions,
        paths,
      });
      polygonRef.current = polygon;

      // Ajustar bounds
      const bounds = new google.maps.LatLngBounds();
      paths.forEach((p) => bounds.extend(p));
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      onBoundsReady?.(bounds);
    } else if (shape === "linear" && Array.isArray(coordinates)) {
      const endpoints = coordinates.map((coord) => ({
        lat: coord.latitude,
        lng: coord.longitude,
      }));
      const storedAxis = Array.isArray(geofence.coordinates_dense)
        ? geofence.coordinates_dense.map((coord) => ({
            lat: coord.latitude,
            lng: coord.longitude,
          }))
        : [];
      const axis = storedAxis.length >= 2 ? storedAxis : endpoints;
      const buffer = geofence.buffer ?? LINEAR_BUFFER_DEFAULT;

      axisLineRef.current = new google.maps.Polyline({
        path: axis,
        strokeColor: colorSet.stroke,
        strokeOpacity: 0.9,
        strokeWeight: 4,
        clickable: false,
        map,
      });

      corridorRef.current = new google.maps.Polygon({
        paths: buildCorridorRing(axis, buffer),
        strokeColor: colorSet.stroke,
        strokeOpacity: 0.7,
        strokeWeight: 1,
        fillColor: colorSet.fill,
        fillOpacity: 0.25,
        clickable: false,
        map,
      });

      const bounds = new google.maps.LatLngBounds();
      corridorRef.current.getPath().forEach((p) => bounds.extend(p));
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      onBoundsReady?.(bounds);
    }

    return () => {
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
      if (axisLineRef.current) {
        axisLineRef.current.setMap(null);
        axisLineRef.current = null;
      }
      if (corridorRef.current) {
        corridorRef.current.setMap(null);
        corridorRef.current = null;
      }
    };
  }, [map, geofence, colorSet, onBoundsReady]);

  // Renderizar markers para polygonal y linear
  if (geofence.shape !== "circular" && Array.isArray(geofence.coordinates)) {
    return (
      <>
        {geofence.coordinates.map((coord, index) => (
          <Marker
            key={`marker-${index}`}
            position={{ lat: coord.latitude, lng: coord.longitude }}
            icon={
              typeof google !== "undefined" && google?.maps?.SymbolPath
                ? {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 8,
                    fillColor: colorSet.stroke,
                    fillOpacity: 1,
                    strokeWeight: 0,
                  }
                : undefined
            }
            label={
              geofence.shape === "linear"
                ? {
                    text: String(index + 1),
                    color: "white",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }
                : undefined
            }
          />
        ))}
      </>
    );
  }

  return null;
};

// Componente de miniatura de mapa estático
const GeofenceThumbnail = ({
  geofence,
  isActive,
  isDarkMode,
  onClick,
}: {
  geofence: Geofence;
  isActive: boolean;
  isDarkMode: boolean;
  onClick: () => void;
}) => {
  // Generar URL de imagen estática de Google Maps
  const getStaticMapUrl = useCallback(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    const { shape, coordinates, type } = geofence;
    const color = (typeColors[type] || typeColors.default).replace("#", "0x");

    let center = "";
    let path = "";
    let markers = "";

    if (shape === "circular" && "radius" in coordinates) {
      center = `${coordinates.latitude},${coordinates.longitude}`;
      markers = `color:${color}|${coordinates.latitude},${coordinates.longitude}`;
    } else if (Array.isArray(coordinates) && coordinates.length > 0) {
      const coords = coordinates.map((c) => `${c.latitude},${c.longitude}`);
      center = coords[0];

      if (shape === "polygonal") {
        path = `color:${color}80|fillcolor:${color}40|weight:2|${coords.join(
          "|"
        )}|${coords[0]}`;
      } else {
        path = `color:${color}|weight:3|${coords.join("|")}`;
      }
    }

    const baseUrl = "https://maps.googleapis.com/maps/api/staticmap";
    const params = new URLSearchParams({
      size: "200x120",
      maptype: "roadmap",
      key: apiKey,
    });

    if (center) params.append("center", center);
    if (markers) params.append("markers", markers);
    if (path) params.append("path", path);
    if (!markers && !path) params.append("zoom", "12");

    return `${baseUrl}?${params.toString()}`;
  }, [geofence]);

  // Obtener dirección para mostrar
  const getDisplayAddress = () => {
    return geofence.address || "Sin dirección";
  };

  // Obtener info adicional según el tipo de forma
  const getShapeInfo = () => {
    const { shape, coordinates } = geofence;
    if (shape === "circular" && "radius" in coordinates) {
      return `Radio: ${coordinates.radius} m`;
    }
    if (shape === "polygonal" && geofence.area) {
      return `Área: ${geofence.area.toLocaleString("es-ES")} m²`;
    }
    if (shape === "linear" && Array.isArray(coordinates)) {
      return `${coordinates.length} puntos`;
    }
    return "";
  };

  return (
    <div
      onClick={onClick}
      className={`flex-shrink-0 w-64 rounded-lg overflow-hidden cursor-pointer transition-all duration-200 ${
        isActive
          ? "ring-2 ring-primary-500 ring-offset-2"
          : isDarkMode
          ? "ring-1 ring-gray-600 hover:ring-gray-500"
          : "ring-1 ring-gray-200 hover:ring-gray-300"
      } ${isDarkMode ? "bg-dark-card" : "bg-white"}`}
    >
      {/* Imagen del mapa */}
      <div className="w-full h-28 overflow-hidden">
        <img
          src={getStaticMapUrl()}
          alt={geofence.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>

      {/* Info de la geocerca */}
      <div className="p-3 space-y-1">
        <p
          className={`text-xs font-medium truncate ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          {geofence.name}
        </p>
        <p
          className={`text-xs truncate ${
            isDarkMode ? "text-gray-400" : "text-gray-600"
          }`}
          title={getDisplayAddress()}
        >
          Dirección:{" "}
          <span className="font-normal">
            {getDisplayAddress().length > 25
              ? getDisplayAddress().substring(0, 25) + "..."
              : getDisplayAddress()}
          </span>
        </p>
        <p
          className={`text-xs ${
            isDarkMode ? "text-gray-400" : "text-gray-600"
          }`}
        >
          <span className="font-medium">Geocerca:</span>{" "}
          {getShapeText(geofence.shape)}
        </p>
        {getShapeInfo() && (
          <p
            className={`text-xs ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            {getShapeInfo()}
          </p>
        )}
      </div>
    </div>
  );
};

// Componente principal
export function GeofenceViewerModal({
  isOpen,
  geofences,
  selectedGeofence,
  isDarkMode,
  onClose,
}: GeofenceViewerModalProps) {
  const [activeGeofence, setActiveGeofence] = useState<Geofence | null>(
    selectedGeofence
  );
  const sliderRef = useRef<HTMLDivElement>(null);

  // Calcular distancia y tiempo para geocercas lineales
  const linearInfo = useMemo(() => {
    if (
      !activeGeofence ||
      activeGeofence.shape !== "linear" ||
      !Array.isArray(activeGeofence.coordinates)
    ) {
      return { distance: 0, time: "Calculando..." };
    }

    const distanceInMeters = calculateLinearDistance(
      activeGeofence.coordinates
    );
    const distanceInKm = distanceInMeters / 1000;
    const estimatedTime = calculateEstimatedTime(distanceInKm);

    return {
      distance: distanceInKm,
      time: estimatedTime,
    };
  }, [activeGeofence]);

  // Actualizar geocerca activa cuando cambia la seleccionada
  useEffect(() => {
    if (selectedGeofence) {
      setActiveGeofence(selectedGeofence);
    }
  }, [selectedGeofence]);

  // Scroll automático al item activo
  useEffect(() => {
    if (activeGeofence && sliderRef.current) {
      const activeIndex = geofences.findIndex(
        (g) => g.id === activeGeofence.id
      );
      if (activeIndex !== -1) {
        const itemWidth = 256 + 16; // w-64 + gap-4
        const scrollPosition =
          activeIndex * itemWidth -
          sliderRef.current.clientWidth / 2 +
          itemWidth / 2;
        sliderRef.current.scrollTo({
          left: Math.max(0, scrollPosition),
          behavior: "smooth",
        });
      }
    }
  }, [activeGeofence, geofences]);

  // Handler para cambiar geocerca
  const handleSelectGeofence = useCallback((geofence: Geofence) => {
    setActiveGeofence(geofence);
  }, []);

  // Obtener centro inicial del mapa
  const getInitialCenter = useCallback(() => {
    if (!activeGeofence) return { lat: 19.4326, lng: -99.1332 };

    const { shape, coordinates } = activeGeofence;

    if (shape === "circular" && "radius" in coordinates) {
      return { lat: coordinates.latitude, lng: coordinates.longitude };
    }

    if (Array.isArray(coordinates) && coordinates.length > 0) {
      return { lat: coordinates[0].latitude, lng: coordinates[0].longitude };
    }

    return { lat: 19.4326, lng: -99.1332 };
  }, [activeGeofence]);

  if (!isOpen) return null;

  return (
    <>
      <style>{sliderScrollbarStyles}</style>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <div
          className={`relative w-[95vw] lg:w-[60vw] h-[95vh] rounded-xl overflow-hidden flex flex-col ${
            isDarkMode ? "bg-dark-surface" : "bg-white"
          }`}
        >
          {/* Header */}
          <div
            className={`flex items-center justify-between px-6 py-4 border-b ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="flex items-center gap-4">
              <h2
                className={`text-xl font-semibold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Mapa - {activeGeofence?.name || "Geocercas"}
              </h2>
              {activeGeofence && (
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(
                    activeGeofence.type
                  )}`}
                >
                  {getTypeText(activeGeofence.type)}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? "hover:bg-gray-700 text-gray-400 hover:text-white"
                  : "hover:bg-gray-100 text-gray-600 hover:text-gray-900"
              }`}
            >
              <i className="ri-close-line text-2xl" />
            </button>
          </div>

          {/* Contenido principal */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Mapa principal */}
            <div className="flex-1 relative p-5">
              {/* Info overlay específica por forma - igual que MapPicker */}
              {activeGeofence && activeGeofence.shape === "circular" && (
                <div
                  style={{
                    position: "absolute",
                    left: 25, // 16 + 5px de padding
                    bottom: 25, // 16 + 5px de padding
                    background: "white",
                    borderRadius: 8,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    padding: "5px 10px",
                    minWidth: 220,
                    zIndex: 10,
                  }}
                  className="border border-gray-300"
                >
                  <div className="font-semibold text-gray-800 text-sm mb-1">
                    Centro:{" "}
                    <span className="font-normal text-xs text-gray-700">
                      {activeGeofence.coordinates &&
                      "radius" in activeGeofence.coordinates
                        ? `${activeGeofence.coordinates.latitude.toFixed(
                            4
                          )}, ${activeGeofence.coordinates.longitude.toFixed(
                            4
                          )}`
                        : "-"}
                    </span>
                  </div>
                  <div className="font-semibold text-gray-800 text-sm mb-1">
                    Radio:{" "}
                    <span className="font-normal text-xs text-gray-700">
                      {activeGeofence.coordinates &&
                      "radius" in activeGeofence.coordinates
                        ? `${activeGeofence.coordinates.radius} m`
                        : "-"}
                    </span>
                  </div>
                </div>
              )}

              {activeGeofence &&
                activeGeofence.shape === "linear" &&
                Array.isArray(activeGeofence.coordinates) && (
                  <div
                    style={{
                      position: "absolute",
                      left: 25, // 16 + 5px de padding
                      bottom: 25, // 16 + 5px de padding
                      background: "white",
                      borderRadius: 8,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      padding: "5px 10px",
                      minWidth: 220,
                      zIndex: 10,
                    }}
                    className="border border-gray-300"
                  >
                    <div className="font-semibold text-gray-800 text-sm mb-1">
                      Total de puntos:{" "}
                      <span className="font-normal text-xs text-gray-700">
                        {activeGeofence.coordinates.length}
                      </span>
                    </div>
                    <div className="font-semibold text-gray-800 text-sm mb-1">
                      Distancia estimada:{" "}
                      <span className="font-normal text-xs text-gray-700">
                        {linearInfo.distance > 0
                          ? `${linearInfo.distance.toFixed(1)} km`
                          : "Calculando..."}
                      </span>
                    </div>
                    <div className="font-semibold text-gray-800 text-sm mb-1">
                      Tiempo estimado:{" "}
                      <span className="font-normal text-xs text-gray-700">
                        {linearInfo.time}
                      </span>
                    </div>
                  </div>
                )}

              {activeGeofence &&
                activeGeofence.shape === "polygonal" &&
                Array.isArray(activeGeofence.coordinates) && (
                  <div
                    style={{
                      position: "absolute",
                      left: 25, // 16 + 5px de padding
                      bottom: 25, // 16 + 5px de padding
                      background: "white",
                      borderRadius: 8,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      padding: "5px 10px",
                      minWidth: 220,
                      zIndex: 10,
                    }}
                    className="border border-gray-300"
                  >
                    <div className="font-semibold text-gray-800 text-sm mb-1">
                      Total de puntos:{" "}
                      <span className="font-normal text-xs text-gray-700">
                        {activeGeofence.coordinates.length}
                      </span>
                    </div>
                    <div className="font-semibold text-gray-800 text-sm mb-1">
                      Área:{" "}
                      <span className="font-normal text-xs text-gray-700">
                        {activeGeofence.area && activeGeofence.area > 0
                          ? `${activeGeofence.area.toLocaleString("es-ES", {
                              maximumFractionDigits: 0,
                            })} m²`
                          : "Calculando..."}
                      </span>
                    </div>
                  </div>
                )}

              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "0.75rem",
                  overflow: "hidden",
                }}
              >
                <APIProvider
                  apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                  libraries={["places"]}
                >
                  <Map
                    key={activeGeofence?.id || "default"}
                    defaultZoom={12}
                    defaultCenter={getInitialCenter()}
                    gestureHandling="greedy"
                    mapId="GEOFENCE_VIEWER_MAP"
                    style={{
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    {activeGeofence && (
                      <GeofenceShape geofence={activeGeofence} />
                    )}
                  </Map>
                </APIProvider>
              </div>
            </div>

            {/* Slider de miniaturas */}
            <div
              className={`border-t ${
                isDarkMode
                  ? "border-gray-700 bg-dark-surface"
                  : "border-gray-200 bg-gray-50"
              }`}
            >
              <div
                ref={sliderRef}
                className="flex gap-4 p-4 overflow-x-auto slider-scrollbar"
                style={{ scrollBehavior: "smooth" }}
              >
                {geofences.map((geofence) => (
                  <GeofenceThumbnail
                    key={geofence.id}
                    geofence={geofence}
                    isActive={activeGeofence?.id === geofence.id}
                    isDarkMode={isDarkMode}
                    onClick={() => handleSelectGeofence(geofence)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default GeofenceViewerModal;
